import React, {useState, useRef, useEffect, useContext, useCallback, useMemo} from "react";
import PropTypes from "prop-types";
import {makeStyles, Box, Typography, Link, Grid, Select, MenuItem, FormControl, InputLabel, TextField} from "@material-ui/core";
import {TimePicker} from "@material-ui/pickers";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import moment from "moment";
import ArrowRight1 from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/arrows-diagrams/arrows/arrow-right-1.svg";
import ArrowLeft1 from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/arrows-diagrams/arrows/arrow-left-1.svg";
import {CustomButton, CustomIconButton, StreamlineIcon, ResponsiveDialog} from "@hapinest/theme";
import {LocaleContext} from "../../../store/providers/LocaleProvider";
import {
    useBreakpoints,
    usePdfDocumentsBySlug,
    useResortServices
} from "../../../hooks";
import {useDate, useI18N} from "../../../common/common";
import {ADDITIONAL_SERVICES_LAYOUT, ADDITIONAL_SERVICES_PACKAGE_TYPE, ADDITIONAL_SERVICES_STATUS, TIME_DISPLAY_FORMAT, YEAR_MONTH_DAY_FORMAT} from "../../../common/constant";
import CustomRichText from "../../widget/text/CustomRichText";
import ResortServiceEntry from "./ResortServiceEntry";
import ResortServiceDetailsDialog from "./ResortServiceDetailsDialog";
import {Controller, useForm} from "react-hook-form";
import {useAnalytics} from "../../../store/providers/AnalyticsProvider";
import {withSnackbar} from "../../hoc/SnackbarHOC";

const useStyles = makeStyles((theme) => ({
    ml2: {
        marginLeft: theme.spacing(2)
    },
    nowrap: {
        whiteSpace: "nowrap"
    },
    displayInlineBlock: {
        display: "inline-block"
    },
    sliderContainer: {
        [theme.breakpoints.only("xs")]: {
            marginRight: theme.spacing(-2),
            marginLeft: theme.spacing(-2)
        },
        "& .slick-track": {
            display: "flex",
            margin: "unset !important"
        },
        "& .slick-slide": {
            flex: 1,
            height: "unset !important",
            "&+.slick-slide": {
                [theme.breakpoints.up("sm")]: {
                    marginLeft: theme.spacing(1.5),
                },
            },
            "&>div": {
                height: "100%"
            }
        },
        "& .slick-prev": {
            zIndex: 1,
            left: theme.spacing(1.5),
        },
        "& .slick-next": {
            right: theme.spacing(4),
        },
        "& .slick-prev, & .slick-next": {
            top: "calc(50% - 78px)",
            "& i": {
                color: theme.palette.grey[0]
            },
            "&.slick-disabled button": {
                cursor: "default",
                pointerEvents: "none",
                "& i": {
                    color: theme.palette.grey["200"] + " !important",
                },
            }
        },
        "& .slick-prev::before, & .slick-next::before": {
            content: "close-quote"
        },
        "& img": {
            objectFit: "cover",
            height: theme.spacing(32),
            [theme.breakpoints.up("sm")]: {
                height: theme.spacing(20.25),
                borderRadius: theme.spacing(1)
            },
        },
        "& .ResortServiceEntry-content, & .ResortServiceEntry-actions": {
            [theme.breakpoints.only("xs")]: {
                paddingRight: theme.spacing(2),
                paddingLeft: theme.spacing(2),
            },
        }
    },
    gridContainer: {
        [theme.breakpoints.only("xs")]: {
            width: "unset",
            marginRight: theme.spacing(-2),
            marginLeft: theme.spacing(-2)
        },
        "& .MuiGrid-item": {
            [theme.breakpoints.only("xs")]: {
                marginBottom: theme.spacing(2),
                borderBottom: "1px solid " + theme.palette.borders.line.light
            },
            "& img": {
                [theme.breakpoints.up("sm")]: {
                    borderRadius: theme.spacing(1)
                },
            },
            "& .ResortServiceEntry-content, & .ResortServiceEntry-actions": {
                [theme.breakpoints.only("xs")]: {
                    paddingRight: theme.spacing(2),
                    paddingLeft: theme.spacing(2),
                },
            }
        }
    },
    select: {
        "& .MuiSelect-root": {
            paddingTop: theme.spacing(2.25),
            paddingBottom: theme.spacing(2.25)
        }
    },
    transferExtraInput: {
        background: theme.palette.secondary[50],
        margin: theme.spacing(0, -3, -1, -3),
        padding: theme.spacing(3),
        "& .MuiGrid-root": {
            marginTop: theme.spacing(0),
        },
        "& .MuiFormControl-root, & .MuiSelect-select:focus": {
            backgroundColor: theme.palette.grey[0] + " !important"
        }
    },
    flex1: {
        flex: 1
    },
    bold: {
        fontWeight: "bold"
    }
}));

const ResortServices = ({status, reservation, resort, layout, showSnackbar}) => {

    const classes = useStyles();
    const {isExtraSmallScreen} = useBreakpoints();
    const {i18n} = useI18N();
    const {formatDate} = useDate();
    const {pdfDocuments, getPdfDocuments} = usePdfDocumentsBySlug();
    const {sendCustomEvent, eventNames, getResortAndApartmentData} = useAnalytics();
    const {localeUppercase} = useContext(LocaleContext)[0];
    const {
        resortServices,
        resortServicesLoading,
        requestServiceLoading,
        resortService,
        resortServiceLoading,
        cancelServiceLoading,
        getResortServices,
        getResortService,
        requestService,
        cancelService,
        cancelServiceError
    } = useResortServices();

    const sliderRef = useRef();
    const [isSliderNavVisible, setSliderNavVisible] = useState();
    const [slideIndex, setSlideIndex] = useState(0);
    const [isResortServiceDetailsDialogOpen, setResortServiceDetailsDialogOpen] = useState(false);
    const [selectedResortService, setSelectedResortService] = useState();
    const [arrivalDateOptions, setArrivalDateOptions] = useState();
    const [dialogConfirmationType, setDialogConfirmationType] = useState();
    const [isDialogConfirmationOpen, setDialogConfirmationOpen] = useState(false);

    useEffect(() => {
        if (resort && !resortServicesLoading && !resortServices) {
            getResortServices({
                hapimagResortId: resort?.hapimagResortId,
                reservationObjId: reservation?.reservation_object_id,
                locale: localeUppercase,
                reservationSignature: reservation?.reservation_signature
            });
        }
    }, [resort]);

    useEffect(() => {
        if (cancelServiceError) {
            return showSnackbar({
                type: "warning",
                message: i18n("common.label.generic_error"),
            });
        }
    }, [cancelServiceError]);

    const {
        control,
        formState: { errors, isValid },
        reset,
        getValues,
        trigger
    } = useForm({
        mode: "onChange"
    });

    const sliderSettings = {
        dots: false,
        infinite: false,
        swipe: true,
        variableWidth: true,
        slidesToScroll: 1,
        slidesToShow: 3,
        nextArrow: null,
        prevArrow: null,
        beforeChange: (oldIndex, newIndex) => {
            if(!isExtraSmallScreen()) {
                const slidesToShow = sliderRef?.current?.innerSlider?.props?.slidesToShow;
                if (oldIndex < newIndex) {
                    let index = newIndex + (slidesToShow - 1);
                    if (index > (sliderRef?.current?.props?.children?.length - 1)) {
                        index = sliderRef?.current?.props?.children?.length - 1;
                    }
                    setSlideIndex(index);
                } else if(oldIndex > newIndex) {
                    setSlideIndex(newIndex);
                }
            } else {
                setSlideIndex(newIndex);
            }

        },
        onReInit: () => {
            const slidesToShow = sliderRef?.current?.innerSlider?.props?.slidesToShow;
            if(slidesToShow >= resortServices?.length) {
                setSliderNavVisible(false);
            } else {
                setSliderNavVisible(true);
            }
        },
        responsive: [
            {
                breakpoint: 1279,
                settings: {
                    slidesToShow: 3,
                }
            },
            {
                breakpoint: 959,
                settings: {
                    slidesToShow: 2,
                }
            },
            {
                breakpoint: 599,
                settings: {
                    slidesToScroll: 1,
                    slidesToShow: 1,
                }
            },
        ],
    };

    const conditionsDocumentSlug = "resortAdditionalServicesConditions";

    useEffect(() => {
        setSelectedResortService(null);
        if (resortService && !resortServiceLoading) {
            setSelectedResortService(resortService);
            if(resortService?.packageType === ADDITIONAL_SERVICES_PACKAGE_TYPE.TRANSFER) {
                const reservationDateFrom = reservation?.date_from;
                setArrivalDateOptions([
                    moment(reservationDateFrom)?.subtract(1, "days")?.startOf("day")?.format(YEAR_MONTH_DAY_FORMAT),
                    moment(reservationDateFrom)?.format(YEAR_MONTH_DAY_FORMAT),
                    moment(reservationDateFrom)?.add(1, "days")?.startOf("day")?.format(YEAR_MONTH_DAY_FORMAT),
                ]);
            }
        }
    }, [resortService, resortServiceLoading]);

    const handleSliderNext = () => {
        sliderRef?.current?.slickNext();
        trackServicesEvent(eventNames.SERVICE_SLIDER, null, {slider_direction: "next"});
    };
    const handleSliderPrevious = () => {
        sliderRef?.current?.slickPrev();
        trackServicesEvent(eventNames.SERVICE_SLIDER, null, {slider_direction: "previous"});
    };

    const handleClickService = (entry) => {
        if (!entry?.booked) {
            setResortServiceDetailsDialogOpen(true);
        } else {
            setDialogConfirmationType("CANCEL_SERVICE");
            setDialogConfirmationOpen(true);
        }

        const serviceSysId = entry?.sys?.id;

        if (!resortServiceLoading) {
            getResortService(serviceSysId, localeUppercase);
        }

        if(status === ADDITIONAL_SERVICES_STATUS.PREVIEW) {
            trackServicesEvent(eventNames.SERVICE_SELECT, entry);
        } else if(status === ADDITIONAL_SERVICES_STATUS.READY_TO_REQUEST) {
            if(!pdfDocuments) {
                getPdfDocuments({variables: {slugs: [conditionsDocumentSlug], locale: localeUppercase}});
            }
            trackServicesEvent(entry?.canBeBookedDirectly ? eventNames.SERVICE_RESERVATION_SELECT : eventNames.SERVICE_REQUEST_SELECT, entry);
        }
    };

    const handleResortServiceDetailsDialogClosed = () => {
        reset();
        setResortServiceDetailsDialogOpen(false);
    };

    const handleRequestService = (entry) => {
        let extraInput;
        if(selectedResortService?.packageType === ADDITIONAL_SERVICES_PACKAGE_TYPE.TRANSFER) {
            trigger();
            if(isValid) {
                extraInput = {
                    arrival_date: getValues("arrival_date"),
                    arrival_time: moment(getValues("arrival_time"))?.format(TIME_DISPLAY_FORMAT),
                    flight_no: getValues("flight_no"),
                    comments: getValues("comments")
                };
            }
        } else if(!selectedResortService?.canBeBookedDirectly) {
            extraInput = {
                comments: getValues("comments")
            };
        }

        if (selectedResortService?.packageType !== ADDITIONAL_SERVICES_PACKAGE_TYPE.TRANSFER ||
            (selectedResortService?.packageType === ADDITIONAL_SERVICES_PACKAGE_TYPE.TRANSFER && isValid)
        ) {
            requestService(reservation?.reservation_id, entry, extraInput, window.location.href)
                .then(() => {
                    setDialogConfirmationType("SUCCESS");
                    trackServicesEvent(entry?.canBeBookedDirectly ? eventNames.SERVICE_RESERVATION_CONFIRMED : eventNames.SERVICE_REQUEST_SEND, entry);
                })
                .catch(err => {
                    console.error(err);
                    setDialogConfirmationType("ERROR");
                    trackServicesEvent(entry?.canBeBookedDirectly ? eventNames.SERVICE_RESERVATION_ERROR : eventNames.SERVICE_REQUEST_ERROR, entry, {error_code: err?.status});
                })
                .finally(() => {
                    handleResortServiceDetailsDialogClosed();
                    setDialogConfirmationOpen(true);
                });
        }
    };

    const handleCancelService = (entry) => {
        cancelService(reservation?.reservation_id, entry).then(() => {
            setDialogConfirmationOpen(false);
            trackServicesEvent(eventNames.SERVICE_RESERVATION_CANCELED, entry);
        });
    };

    const handleDismissDialogConfirmation = () => {
        setDialogConfirmationOpen(false);
    };

    const sliderButtons = (
        <>
            <CustomIconButton
                color={"primary"}
                variant={"contained"}
                onClick={handleSliderPrevious}
                disabled={slideIndex === 0}
            >
                <StreamlineIcon><ArrowLeft1/></StreamlineIcon>
            </CustomIconButton>
            <CustomIconButton
                color={"primary"}
                variant={"contained"}
                className={classes.ml2}
                onClick={handleSliderNext}
                disabled={slideIndex === resortServices?.length - 1}
            >
                <StreamlineIcon><ArrowRight1/></StreamlineIcon>
            </CustomIconButton>
        </>
    );

    const dialogActionsPreview = (
        selectedResortService?.canBeBookedDirectly === null ? null : (
            <>
                {selectedResortService?.canBeBookedDirectly ? (
                    <Typography
                        variant="body1"
                    >
                        {i18n("view.resort.services.request_after_booking", {resortName: selectedResortService?.resort?.resortName})}
                    </Typography>
                ) : (
                    <>
                        <Typography
                            variant="subtitle1"
                        >
                            {i18n("view.resort.services.interested_contact")}
                        </Typography>
                        <Box mt={3}>
                            {!selectedResortService?.externalServiceInfo ? (
                                <>
                                    <Typography
                                        variant="subtitle1"
                                        className={classes.displayInlineBlock}
                                    >
                                        {i18n("common.label.phone") + ":"}&nbsp;
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        className={classes.displayInlineBlock}
                                    >
                                        {selectedResortService?.resort?.resortContactDetails?.resortPhoneNumber}
                                    </Typography>

                                    <Box>
                                        <Typography
                                            variant="subtitle1"
                                            className={classes.displayInlineBlock}
                                        >
                                            {i18n("view.legal_pages.imprint.email") + ":"}&nbsp;
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            className={classes.displayInlineBlock}
                                        >
                                            {selectedResortService?.resort?.resortContactDetails?.resortEmailAddress}
                                        </Typography>
                                    </Box>
                                </>
                            ) : (
                                <CustomRichText document={selectedResortService?.externalServiceInfo?.json}/>
                            )}
                        </Box>
                        {selectedResortService?.bookingInformation && (
                            <Box mt={3}>
                                <Typography
                                    variant="subtitle1"
                                >
                                    {selectedResortService.bookingInformation}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </>
        )
    );

    const dialogConfirmation = {
        SUCCESS: {
            disableBackdropClick: true,
            hasCloseButton: true,
            content: (
                <>
                    <Box
                        display="flex"
                        alignItems="center"
                        flexDirection="column"
                        textAlign="center"
                    >
                        <img
                            src="/images/request-received-icon.svg"
                            alt="request-received"
                            width={100}
                            height={100}
                        />
                        <Box mt={3}>
                            <Typography
                                variant="h3"
                            >
                                {selectedResortService?.canBeBookedDirectly ? (
                                    i18n("view.reservations.services.reservation_has_been_made")
                                ) : (
                                    i18n("view.reservations.services.request_has_been_made")
                                )}
                            </Typography>
                        </Box>
                        <Box mt={3}>
                            <Typography
                                variant="body1"
                            >
                                {i18n("view.reservations.services.receive_confirmation_email")}
                            </Typography>
                        </Box>
                    </Box>
                </>
            ),
            actions: (
                <>
                    <CustomButton
                        autoFocus
                        onClick={handleDismissDialogConfirmation}
                        variant="contained"
                    >
                        {i18n("common.label.ok")}
                    </CustomButton>
                </>
            )
        },
        ERROR: {
            disableBackdropClick: true,
            hasCloseButton: false,
            content: (
                <>
                    <Box
                        display="flex"
                        alignItems="center"
                        flexDirection="column"
                        textAlign="center"
                    >
                        <img
                            src="/images/car_error.svg"
                            alt="cancel-booking-icon"
                            width={100}
                            height={100}
                        />
                        <Box mt={3}>
                            <Typography
                                variant="h3"
                            >
                                {i18n("common.label.could_not_complete_request")}
                            </Typography>
                        </Box>
                        <Box mt={2}>
                            <Typography
                                variant="body1"
                            >
                                {i18n("common.label.system_plunge")}
                            </Typography>
                        </Box>
                    </Box>
                </>
            ),
            actions: (
                <>
                    <CustomButton
                        autoFocus
                        onClick={handleDismissDialogConfirmation}
                        variant="outlined"
                        color="black"
                    >
                        {i18n("common.label.please_try_again")}
                    </CustomButton>
                </>
            )
        },
        CANCEL_SERVICE: {
            disableBackdropClick: true,
            hasCloseButton: true,
            content: (
                <>
                    <Box
                        display="flex"
                        alignItems="center"
                        flexDirection="column"
                        textAlign="center"
                    >
                        <img
                            src="/images/remove-request-question.svg"
                            alt="cancel-service-icon"
                            width={100}
                            height={100}
                        />
                        <Box mt={3}>
                            <Typography
                                variant="h3"
                            >
                                {i18n("view.resort.services.cancel_service_dialog.title")}
                            </Typography>
                        </Box>
                        <Box mt={2}>
                            <Typography
                                variant="body1"
                            >
                                {i18n("view.resort.services.cancel_service_dialog.msg")}
                            </Typography>
                        </Box>
                    </Box>
                </>
            ),
            actions: (
                <>
                    <CustomButton
                        variant="outlined"
                        color="black"
                        onClick={handleDismissDialogConfirmation}
                    >
                        {i18n("view.resort.services.cancel_service_dialog.abort_cta")}
                    </CustomButton>
                    <CustomButton
                        autoFocus
                        variant="contained"
                        loading={resortServiceLoading || cancelServiceLoading}
                        onClick={() => handleCancelService(selectedResortService)}
                    >
                        {i18n("view.reservations.yes_cancel")}
                    </CustomButton>
                </>
            )
        }
    };

    const servicesTermsHyperlink = (str) => {
        return (
            <Link
                color={"inherit"}
                underline="always"
                key={str}
                href={pdfDocuments?.[0]?.pdfDocumentCollection?.items[0]?.url}
                className={classes.bold}
                target="_blank"
            >
                {str}
            </Link>
        );
    };

    const trackServicesEvent = useCallback((eventName, resortService, extraData) => {
        if(eventName && resort && sendCustomEvent) {
            const data = {
                ...getResortAndApartmentData(resort),
                ...extraData
            };

            if(resortService) {
                data.service_name = resortService.title_en_gb;
                data.service_code = resortService.serviceNumberOptionsCollection?.items?.length > 0 ? resortService.serviceNumberOptionsCollection.items.map(option => option?.serviceNumber)?.join("|") : undefined;
            }

            if(reservation) {
                data.guests_no = reservation.guests;
                data.arrival_date = reservation.date_from;
                data.departure_date = reservation.date_to;
            }

            sendCustomEvent({
                event_name: eventName,
                [eventName]: data
            });
        }
    }, [sendCustomEvent, reservation, resort]);

    const filteredResortServicesByAvailabilityDates = useMemo(() => {
        const reservationDateFrom = moment(reservation?.date_from);
        const reservationDateTo = moment(reservation?.date_to);

        return resortServices?.filter((service) => {
            // We show all services that do not have defined available from/to days
            if (!service.availableFrom || !service.availableTo) {
                return true;
            }
            const serviceAvailableFrom = moment(service.availableFrom.split("T")[0]);
            const serviceAvailableTo = moment(service.availableTo.split("T")[0]);

            // Checks if there is an overlap between reservation days range and service availability days range
            return serviceAvailableFrom.isSameOrBefore(reservationDateTo, "day") && serviceAvailableTo.isSameOrAfter(reservationDateFrom, "day");
        });
    }, [reservation, resortServices]);

    return (
        <>

            {isSliderNavVisible &&
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    className={classes.nowrap}
                    mb={5}
                >
                    {sliderButtons}
                </Box>
            }

            {layout === ADDITIONAL_SERVICES_LAYOUT.GRID ? (
                <Grid
                    container
                    className={classes.gridContainer}
                >
                    {filteredResortServicesByAvailabilityDates?.map((item, index) => (
                        <Grid
                            key={index}
                            item
                            xs={12}
                            sm={6}
                            md={4}
                        >
                            <ResortServiceEntry
                                entry={item}
                                status={status}
                                onClick={handleClickService}
                            />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Slider
                    {...sliderSettings}
                    ref={sliderRef}
                    className={classes.sliderContainer}
                >
                    {resortServices?.map((item, index) => (
                        <ResortServiceEntry
                            key={index}
                            entry={item}
                            status={status}
                            onClick={handleClickService}
                        />
                    ))}
                </Slider>
            )}

            <ResortServiceDetailsDialog
                isOpen={isResortServiceDetailsDialogOpen}
                onClose={handleResortServiceDetailsDialogClosed}
                entry={selectedResortService}
                extraInput={(() => {
                    switch (status) {
                        case ADDITIONAL_SERVICES_STATUS.READY_TO_REQUEST:
                            return (
                                <>
                                    {selectedResortService?.canBeBookedDirectly !== null && (
                                        <Box
                                            className={(selectedResortService?.packageType === ADDITIONAL_SERVICES_PACKAGE_TYPE.TRANSFER || !selectedResortService?.canBeBookedDirectly) ? classes.transferExtraInput : ""}
                                        >
                                            <Grid
                                                container
                                                direction="row"
                                                spacing={3}
                                            >
                                                {selectedResortService?.packageType === ADDITIONAL_SERVICES_PACKAGE_TYPE.TRANSFER &&
                                                <>
                                                    <Grid
                                                        item
                                                        xs={6}
                                                        sm={4}
                                                    >
                                                        <FormControl
                                                            variant="outlined"
                                                            fullWidth
                                                            error={errors?.arrival_date !== undefined}
                                                        >
                                                            <InputLabel>
                                                                {i18n("view.reservations.services.pickup_date")}
                                                            </InputLabel>
                                                            <Controller
                                                                name="arrival_date"
                                                                defaultValue={moment(reservation?.date_from)?.format(YEAR_MONTH_DAY_FORMAT)}
                                                                control={control}
                                                                render={({field}) => (
                                                                    <Select
                                                                        {...field}
                                                                        label={i18n("view.reservations.services.pickup_date")}
                                                                        fullWidth
                                                                        className={classes.select}
                                                                        variant="outlined"
                                                                        id="arrival_date"
                                                                    >
                                                                        {arrivalDateOptions?.map((entry, index) => {
                                                                            return (
                                                                                <MenuItem
                                                                                    key={index}
                                                                                    value={entry}
                                                                                >
                                                                                    {formatDate(entry, { month: "numeric", day: "numeric", year: "numeric" })}
                                                                                </MenuItem>
                                                                            );
                                                                        })}
                                                                    </Select>
                                                                )}
                                                            />
                                                        </FormControl>
                                                    </Grid>

                                                    <Grid
                                                        item
                                                        xs={6}
                                                        sm={4}
                                                    >
                                                        <Controller
                                                            name="arrival_time"
                                                            defaultValue={null}
                                                            control={control}
                                                            rules={{
                                                                required: true,
                                                                validate: {
                                                                    invalidFormat: (value) => value instanceof Date && !isNaN(value),
                                                                }
                                                            }}
                                                            render={({field}) => (
                                                                <TimePicker
                                                                    {...field}
                                                                    showToolbar={false}
                                                                    okLabel={i18n("common.label.ok")}
                                                                    cancelLabel={i18n("common.label.cancel")}
                                                                    variant="outlined"
                                                                    fullWidth
                                                                    label={i18n("view.reservations.services.pickup_time")}
                                                                    error={errors?.arrival_time !== undefined || errors?.arrival_time?.invalidFormat}
                                                                    invalidDateMessage={i18n("common.form.validation_errors.invalid_format")}
                                                                    id="arrival_time"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>

                                                    <Grid
                                                        item
                                                        xs={12}
                                                        sm={4}
                                                    >
                                                        <Controller
                                                            name="flight_no"
                                                            defaultValue=""
                                                            control={control}
                                                            rules={{
                                                                required: true
                                                            }}
                                                            render={({field}) => (
                                                                <TextField
                                                                    {...field}
                                                                    label={i18n("view.reservations.services.flight_number")}
                                                                    variant="outlined"
                                                                    fullWidth
                                                                    id="flight_no"
                                                                    error={errors?.flight_no !== undefined}
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                </>
                                                }

                                                {!selectedResortService?.canBeBookedDirectly &&
                                                <Grid
                                                    item
                                                    xs={12}
                                                >
                                                    <Controller
                                                        name="comments"
                                                        control={control}
                                                        render={({field}) => (
                                                            <TextField
                                                                {...field}
                                                                id="comments"
                                                                variant="outlined"
                                                                fullWidth
                                                                multiline
                                                                rows={3}
                                                                label={selectedResortService?.placeholderForComments || i18n("view.reservations.services.comments")}
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                }
                                            </Grid>
                                        </Box>
                                    )}
                                </>
                            );
                        case ADDITIONAL_SERVICES_STATUS.PREVIEW:
                        default:
                            return null;
                    }
                })()}
                actions={(() => {
                    switch (status) {
                        case ADDITIONAL_SERVICES_STATUS.READY_TO_REQUEST:
                            return (
                                selectedResortService?.canBeBookedDirectly === null ? null : (
                                    <Grid
                                        container
                                        direction={"row"}
                                        alignItems={"center"}
                                        justify={"space-between"}
                                        spacing={1}
                                    >
                                        <Grid
                                            item
                                            className={classes.flex1}
                                        >
                                            <Typography
                                                variant="caption"
                                            >
                                                {i18n("view.reservations.services.order_services_agree_terms_conditions", {hyperlink: servicesTermsHyperlink})}
                                            </Typography>
                                        </Grid>

                                        <Grid item>
                                            <CustomButton
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleRequestService(selectedResortService)}
                                                loading={requestServiceLoading}
                                            >
                                                {selectedResortService?.canBeBookedDirectly ? (
                                                    i18n("common.label.reserve")
                                                ) : (
                                                    i18n("common.label.request")
                                                )}
                                            </CustomButton>
                                        </Grid>
                                    </Grid>
                                )
                            );
                        case ADDITIONAL_SERVICES_STATUS.PREVIEW:
                        default:
                            return dialogActionsPreview;
                    }
                })()}
            />

            {dialogConfirmationType &&
                <ResponsiveDialog
                    open={isDialogConfirmationOpen}
                    disableBackdropClick={dialogConfirmation[dialogConfirmationType].disableBackdropClick}
                    handleClose={handleDismissDialogConfirmation}
                    hasCloseButton={dialogConfirmation[dialogConfirmationType].hasCloseButton}
                    content={dialogConfirmation[dialogConfirmationType].content}
                    actions={dialogConfirmation[dialogConfirmationType].actions}
                />
            }
        </>
    );
};

ResortServices.propTypes = {
    status: PropTypes.number,
    reservation: PropTypes.object,
    resort: PropTypes.object,
    layout: PropTypes.oneOf([ADDITIONAL_SERVICES_LAYOUT.SLIDER, ADDITIONAL_SERVICES_LAYOUT.GRID]),
    showSnackbar: PropTypes.func
};

ResortServices.defaultProps = {
    status: ADDITIONAL_SERVICES_STATUS.PREVIEW,
    layout: ADDITIONAL_SERVICES_LAYOUT.SLIDER
};

export default withSnackbar(ResortServices);

